import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { Assessment, Clinic, IntakeForm } from 'src/API';
import { filter, map, switchMap, catchError } from 'rxjs/operators';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { compareDateStrings } from '../api/date.util';
import { Patient } from '../api/patient.service';
import { PampClinicConfigService } from '../api/pamp-clinic-config.service';
import { PampClinicConfigLoadStatus } from '../models/pamp-clinic-config.model';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private routerSubscription: Subscription;

  private currentUrlSubject = new BehaviorSubject<string>('');
  public currentUrl$ = this.currentUrlSubject.asObservable();

  private currentPatientSubject = new BehaviorSubject<Patient | null>(null);
  public currentPatient$ = this.currentPatientSubject.asObservable();

  private patientsSubject = new BehaviorSubject<Patient[]>([]);
  public patients$ = this.patientsSubject.asObservable();

  private assessmentsSubject = new BehaviorSubject<Assessment[]>([]);
  public assessments$ = this.assessmentsSubject.asObservable();

  private currentIntakeFormSubject = new BehaviorSubject<IntakeForm | null>(null);
  public currentIntakeForm$ = this.currentIntakeFormSubject.asObservable();

  private intakeFormsSubject = new BehaviorSubject<IntakeForm[]>([]);
  public intakeForms$ = this.intakeFormsSubject.asObservable();

  private clinicSubject = new BehaviorSubject<Clinic>(null);
  public clinic$ = this.clinicSubject.asObservable();

  private isPatientAppActiveSubject = new BehaviorSubject<boolean>(false);
  public isPatientAppActive$ = this.isPatientAppActiveSubject.asObservable();

  private openInIframeSubject = new BehaviorSubject<boolean>(true);
  public openInIframe$ = this.openInIframeSubject.asObservable();

  private pampClinicConfigLoadStatusSubject = new BehaviorSubject<PampClinicConfigLoadStatus>(
    'loading'
  );
  public pampClinicConfigLoadStatus$ = this.pampClinicConfigLoadStatusSubject.asObservable();

  private isPampClinicConfigMissing(error: HttpErrorResponse): boolean {
    return !!error && error.status === 404;
  }

  public getOpenInIframeValue(): boolean {
    return this.openInIframeSubject.getValue();
  }

  public getIsPatientAppActive(): boolean {
    return this.isPatientAppActiveSubject.getValue();
  }

  private debugMode = false; // For testing

  constructor(private router: Router, private pampClinicConfigService: PampClinicConfigService) {
    this.subscribeToRouterEvents();

    this.currentPatient$.pipe(untilDestroyed(this)).subscribe(updatedPatient => {
      this.updatePatients(updatedPatient);
    });

    this.currentIntakeForm$.pipe(untilDestroyed(this)).subscribe(updatedIntakeForm => {
      this.updateIntakeForms(updatedIntakeForm);
    });

    this.clinic$
      .pipe(
        filter(clinic => !!clinic && !!clinic.id),
        switchMap(clinic =>
          this.pampClinicConfigService.getPampClinicConfig(clinic.id).pipe(
            map(PAMPClinicConfig => ({ PAMPClinicConfig, hasLoadFailed: false })),
            catchError((error: HttpErrorResponse) =>
              of({ PAMPClinicConfig: null, hasLoadFailed: !this.isPampClinicConfigMissing(error) })
            )
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(({ PAMPClinicConfig, hasLoadFailed }) => {
        if (PAMPClinicConfig) {
          const isActive = PAMPClinicConfig.isActive;
          this.isPatientAppActiveSubject.next(!!isActive);
          this.openInIframeSubject.next(!!PAMPClinicConfig.openInIframe);
        } else {
          this.isPatientAppActiveSubject.next(false);
          this.openInIframeSubject.next(false);
        }
        this.pampClinicConfigLoadStatusSubject.next(hasLoadFailed ? 'failed' : 'loaded');
      });

    if (this.debugMode) {
      this.currentPatient$.pipe(untilDestroyed(this)).subscribe(patient => {
        console.log(`Current patient updated`, patient);
      });
      // this.patients$.pipe(untilDestroyed(this)).subscribe(p => {
      //   console.log(`All patients updated`, p);
      // });
      // this.intakeForms$.pipe(untilDestroyed(this)).subscribe(intakeForms => {
      //   console.log('All intake forms updated', intakeForms);
      // });
      // this.currentIntakeForm$.pipe(untilDestroyed(this)).subscribe(intakeForm => {
      //   console.log('Current intake form updated', intakeForm);
      // });
      this.clinic$.pipe(untilDestroyed(this)).subscribe(clinic => {
        console.log('Clinic updated', clinic);
      });
    }
  }

  private subscribeToRouterEvents(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe(event => {
        const routerState = this.router.routerState;
        const root = routerState.snapshot.root;
        const routeData = this.getAllRouteData(root);
        // May need this for workflow assistant
        // console.log('Route Data:', routeData);
        // console.log('Router event', event);
        //         const navigation = this.router.getCurrentNavigation();
        // const stateData =
        //   navigation && navigation.extras && navigation.extras.state ? navigation.extras.state : {};
        // console.log('state data', stateData);
        // this.currentUrlSubject.next(event.url);

        this.updateAppStateViaRouteData(routeData);
      });
  }

  private getAllRouteData = (route: ActivatedRouteSnapshot) => {
    let data = { ...route.data };
    if (route.firstChild) {
      data = { ...data, ...this.getAllRouteData(route.firstChild) };
    }
    return data;
  };

  private updateAppStateViaRouteData(routeData: any) {
    this.updateCurrentPatientViaRouteData(routeData);
    this.updateCurrentIntakeFormViewRouteData(routeData);
  }

  private updateCurrentIntakeFormViewRouteData(routeData) {
    const intakeForm = routeData.intakeForm;
    if (!intakeForm) {
      this.currentIntakeFormSubject.next(null);
      return;
    }
    this.currentIntakeFormSubject.next(intakeForm);
  }

  private updateCurrentPatientViaRouteData(routeData: any) {
    const routeDataPatient = routeData.patient;
    if (!routeDataPatient) {
      return;
    }
    if (this.isStaleResolvedPatient(routeDataPatient)) {
      return;
    }
    this.currentPatientSubject.next(routeDataPatient);
  }

  private isStaleResolvedPatient(routeDataPatient: Patient): boolean {
    const currentPatient = this.getCurrentPatient();
    if (!currentPatient || currentPatient.id !== routeDataPatient.id) {
      return false;
    }
    if (!routeDataPatient.updatedAt || !currentPatient.updatedAt) {
      return false;
    }
    return compareDateStrings(routeDataPatient.updatedAt, currentPatient.updatedAt) < 0;
  }

  public onOperationIntercepted(operationName, operationData) {
    this.updatePatientsState(operationName, operationData);
    this.updateAssessmentsState(operationName, operationData);
    this.updateIntakeFormsState(operationName, operationData);
    this.updateClinicState(operationName, operationData);
  }

  private updateAssessmentsState(operationName, operationData) {
    switch (operationName) {
      case 'CreateAssessment': {
        this.updateAssessments(operationData['createAssessment']);
        break;
      }
      case 'UpdateAssessment': {
        this.updateAssessments(operationData['updateAssessment']);
        break;
      }
    }
  }

  private updateClinicState(operationName, operationData) {
    let currentClinic = this.getClinic();
    switch (operationName) {
      case 'GetAllInfo': {
        this.clinicSubject.next({
          ...currentClinic,
          ...(operationData['me'] ? operationData['me'].clinic : {})
        });
        break;
      }
      case 'UpdateClinic': {
        const currentClinicSetup =
          (currentClinic && currentClinic.clinicSetup) || ({} as Clinic['clinicSetup']);
        const incomingClinicSetup =
          (operationData['updateClinic'] && operationData['updateClinic'].clinicSetup) || {};
        this.clinicSubject.next({
          ...currentClinic,
          ...operationData['updateClinic'],
          clinicSetup: {
            ...currentClinicSetup,
            ...incomingClinicSetup,
            layouts: this.replaceItems(
              currentClinicSetup.layouts || [],
              incomingClinicSetup.layouts || []
            )
          }
        });
        break;
      }
      case 'UpdateClinicSetup': {
        this.clinicSubject.next({
          ...currentClinic,
          clinicSetup: operationData['updateClinicSetup']
        });
        break;
      }
    }
  }

  private updatePatientsState(operationName, operationData) {
    switch (operationName) {
      case 'UpdatePatient': {
        const incomingPatient = operationData['updatePatient'];
        let currentPatient = this.getCurrentPatient();
        if (currentPatient && incomingPatient) {
          if (incomingPatient.id === currentPatient.id) {
            // Update current patient if id matches
            currentPatient = {
              ...(currentPatient || {}),
              ...incomingPatient,
              questionnaireRequests: this.mergeItems(
                currentPatient.questionnaireRequests,
                incomingPatient.questionnaireRequests
              ),
              assessments: this.mergeItems(currentPatient.assessments, incomingPatient.assessments)
            };
            this.currentPatientSubject.next(currentPatient);
            break;
          }
        }

        this.updatePatients(incomingPatient);
        break;
      }
      case 'CreatePatient': {
        const newPatient = operationData['createPatient'];
        this.updatePatients(newPatient);
        break;
      }
      case 'UpdateQuestionnaireRequest': {
        const updatedQuestionnaireRequest = operationData['updateQuestionnaireRequest'];
        const currentPatient = this.getCurrentPatient();
        if (!currentPatient) {
          break;
        }
        if (currentPatient.questionnaireRequests && currentPatient.questionnaireRequests.items) {
          const questionnaireRequests = currentPatient.questionnaireRequests.items;
          for (let i = 0; i < questionnaireRequests.length; i++) {
            if (questionnaireRequests[i].id === updatedQuestionnaireRequest.id) {
              questionnaireRequests[i] = updatedQuestionnaireRequest;
            }
          }
        }
        this.syncAssessmentQuestionnaireLinks(currentPatient, updatedQuestionnaireRequest);
        this.currentPatientSubject.next(currentPatient);
        break;
      }
      case 'GetPatientsForClinic': {
        this.patientsSubject.next(operationData.getClinic.patients.items);
        break;
      }
    }
  }

  // A questionnaire carries its assessment, and an assessment carries its own list of linked
  // questionnaires. Only the first side was being kept in step, so linking a questionnaire left
  // the assessment's list stale until a full page load refetched it.
  private syncAssessmentQuestionnaireLinks(
    currentPatient: any,
    updatedQuestionnaireRequest: any
  ): void {
    // The limited mutation does not select `assessment`. Treating that absence as "unlinked"
    // would strip a link the caller never touched, so only a payload that carries the field
    // is allowed to move a questionnaire between assessments.
    if (!updatedQuestionnaireRequest.hasOwnProperty('assessment')) {
      return;
    }
    if (!currentPatient.assessments || !currentPatient.assessments.items) {
      return;
    }

    const linkedAssessmentId = updatedQuestionnaireRequest.assessment
      ? updatedQuestionnaireRequest.assessment.id
      : null;

    // Each assessment object is shared with PatientService.assessments, so its list is replaced
    // on the object rather than the array being rebuilt.
    currentPatient.assessments.items.forEach(assessment => {
      if (!assessment) {
        return;
      }
      const linkedQuestionnaires =
        assessment.questionnaireRequests && assessment.questionnaireRequests.items
          ? assessment.questionnaireRequests.items
          : [];
      const withoutThisQuestionnaire = linkedQuestionnaires.filter(
        linkedQuestionnaire =>
          linkedQuestionnaire && linkedQuestionnaire.id !== updatedQuestionnaireRequest.id
      );
      const belongsToThisAssessment = !!linkedAssessmentId && assessment.id === linkedAssessmentId;

      if (
        !belongsToThisAssessment &&
        withoutThisQuestionnaire.length === linkedQuestionnaires.length
      ) {
        return;
      }

      assessment.questionnaireRequests = {
        ...(assessment.questionnaireRequests || {}),
        items: belongsToThisAssessment
          ? withoutThisQuestionnaire.concat([updatedQuestionnaireRequest])
          : withoutThisQuestionnaire
      };
    });
  }

  private replaceItems(existing: any[], incoming: any[]): any[] {
    if (!existing || !incoming) return existing || incoming;

    const existingItemsById = new Map<any, any>(existing.map(item => [item.id, item]));

    return incoming.map((incomingItem: any) => {
      const existingItem = existingItemsById.get(incomingItem.id);
      return existingItem ? { ...existingItem, ...incomingItem } : incomingItem;
    });
  }

  private mergeItems(existing: any, incoming: any) {
    if (!existing || !incoming) return existing || incoming;

    const existingItemsById = new Map<any, any>(
      (existing.items || []).map(item => [item.id, item])
    );

    const mergedItems = [
      ...(existing.items || []).filter(item => !(incoming.items || []).some(i => i.id === item.id)),
      ...(incoming.items || []).map(incomingItem => {
        const existingItem = existingItemsById.get(incomingItem.id);
        return existingItem ? { ...existingItem, ...incomingItem } : incomingItem;
      })
    ];

    return {
      ...existing,
      ...incoming,
      items: mergedItems
    };
  }

  private updateIntakeFormsState(operationName, operationData) {
    switch (operationName) {
      case 'ListIntakeFormByClinicIdSortedByCreatedAt': {
        this.intakeFormsSubject.next(
          operationData.listIntakeFormByClinicIdSortedByCreatedAt.items.map(item => ({
            ...item,
            response: JSON.parse(item.response)
          }))
        );
        break;
      }
      case 'GetIntakeForm': {
        const incomingIntakeForm = operationData['getIntakeForm'];
        this.updateIntakeForms(incomingIntakeForm);
        break;
      }
      case 'UpdateIntakeForm': {
        const incomingIntakeForm = operationData['UpdateIntakeForm'];
        let currentIntakeForm = this.getCurrentIntakeForm();
        if (currentIntakeForm && incomingIntakeForm) {
          if (incomingIntakeForm.id === currentIntakeForm.id) {
            currentIntakeForm = {
              ...(currentIntakeForm || {}),
              ...incomingIntakeForm,
              response: safeJsonParse(incomingIntakeForm.response || '{}')
            };
            this.currentIntakeFormSubject.next(currentIntakeForm);
            break;
          }
        }

        this.updateIntakeForms(incomingIntakeForm);
        break;
      }
    }
  }

  private updatePatients(patient: Patient) {
    this.updateOrAddToArray(patient, this.getPatients(), this.patientsSubject);
  }

  private updateIntakeForms(intakeForm: IntakeForm) {
    this.updateOrAddToArray(intakeForm, this.getIntakeForms(), this.intakeFormsSubject);
  }

  private updateAssessments(assessment: Assessment) {
    this.updateOrAddToArray(assessment, this.getAssessments(), this.assessmentsSubject);
  }

  private updateOrAddToArray(element: any, array: any[], arraySubject: BehaviorSubject<any>) {
    if (!element || !element.id) {
      return;
    }
    const elementIndex = array.findIndex(e => {
      if (!e.id) {
        return false;
      }
      return e.id === element.id;
    });
    if (elementIndex !== -1) {
      array[elementIndex] = { ...array[elementIndex], ...element };
    } else if (elementIndex === -1) {
      array.push(element);
    } else {
      console.error('Error updating array state');
    }
    arraySubject.next(array);
  }

  public getClinic() {
    return this.clinicSubject.getValue();
  }

  public getCurrentPatient() {
    return this.currentPatientSubject.getValue();
  }

  public getPatients() {
    return this.patientsSubject.getValue();
  }

  public getCurrentIntakeForm() {
    return this.currentIntakeFormSubject.getValue();
  }

  public getIntakeForms() {
    return this.intakeFormsSubject.getValue();
  }

  public getAssessments() {
    return this.assessmentsSubject.getValue();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
